import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getJobResults } from '../services/api';
import { 
  formatCurrency, 
  formatPercentage,
  createDetailedAnalysis,
  getPillarColor
} from '../services/formatters';

const ResultsViewer = ({ jobId }) => {
  const [results, setResults] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [jsonParseSuccess, setJsonParseSuccess] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await getJobResults(jobId);
        setResults(response.result);
        
        // Try to extract and process the JSON
        try {
          const processedData = createDetailedAnalysis(response.result);
          if (processedData) {
            setAnalysisData(processedData);
            setJsonParseSuccess(true);
          }
        } catch (jsonError) {
          console.warn('Could not parse JSON from results', jsonError);
          // Don't set error, we'll still show the raw text
        }
        
        setLoading(false);
      } catch (error) {
        setError('Error fetching results');
        setLoading(false);
      }
    };
    
    if (jobId) {
      fetchResults();
    }
  }, [jobId]);

  // Function to detect and format JSON sections within the text
  const formatResults = (text) => {
    if (!text) return '';
    
    // Try to find JSON sections
    const parts = [];
    let currentIndex = 0;
    
    // Simple regex to find potential JSON sections
    const jsonRegex = /\{[\s\S]*?\}/g;
    let match;
    
    while ((match = jsonRegex.exec(text)) !== null) {
      // Add text before the JSON
      if (match.index > currentIndex) {
        parts.push(<span key={`text-${currentIndex}`}>{text.substring(currentIndex, match.index)}</span>);
      }
      
      // Try to parse the JSON
      try {
        const jsonObj = JSON.parse(match[0]);
        parts.push(
          <pre key={`json-${match.index}`} className="json-content bg-gray-50 p-3 rounded-md my-2 overflow-auto">
            {JSON.stringify(jsonObj, null, 2)}
          </pre>
        );
      } catch (e) {
        // If not valid JSON, just add as regular text
        parts.push(<span key={`text-${match.index}`}>{match[0]}</span>);
      }
      
      currentIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push(<span key={`text-${currentIndex}`}>{text.substring(currentIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : text;
  };

  const renderOutput = (output) => {
    // Get the output text with the "Output" key
    const outputText = output["Output "] || output.Output;
    
    return (
      <div className="output-item bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-200" key={outputText}>
        <div className="output-header mb-3">
          <div className="output-title font-medium text-gray-900">{outputText}</div>
          <div className="output-amount text-blue-600 font-semibold mt-1">{output.Amount}</div>
        </div>
        <div className="output-details grid grid-cols-2 gap-2 mb-3">
          <div className="output-label text-sm text-gray-600">Pillar:</div>
          <div className="output-value text-sm font-medium">{output.Pillar}</div>
          <div className="output-label text-sm text-gray-600">Subtag:</div>
          <div className="output-value text-sm font-medium">{output.Subtag}</div>
        </div>
        <div className="output-justification">
          <div className="output-label text-sm text-gray-600 mb-1">Justification:</div>
          <div className="output-value text-sm text-gray-700">{output.Justification}</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-lg font-medium text-gray-700">Loading results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="text-red-800 text-lg font-medium">Error</div>
        <div className="mt-2 text-red-700">{error}</div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="bg-gray-50 p-6 rounded-md text-center">
        <div className="text-lg font-medium text-gray-700">No results available.</div>
      </div>
    );
  }

  // Extract key data for visualization if analysis data is available
  const {
    totalEwsAmount,
    totalNonEwsAmount,
    pillarDistribution,
    pieChartData,
    overallDistributionData,
    groupedActivities,
    nonEwsDetails
  } = analysisData || {};

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4">
        <h2 className="text-xl font-bold">Analysis Results</h2>
        <p className="text-sm opacity-80">Job ID: {jobId}</p>
      </div>
      
      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200">
        {jsonParseSuccess && (
          <>
            <button 
              className={`px-4 py-3 text-sm font-medium ${activeTab === 'summary' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600'}`}
              onClick={() => setActiveTab('summary')}
            >
              Summary
            </button>
            <button 
              className={`px-4 py-3 text-sm font-medium ${activeTab === 'details' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600'}`}
              onClick={() => setActiveTab('details')}
            >
              EWS Details
            </button>
            <button 
              className={`px-4 py-3 text-sm font-medium ${activeTab === 'non-ews' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600'}`}
              onClick={() => setActiveTab('non-ews')}
            >
              Non-EWS
            </button>
          </>
        )}
        <button 
          className={`px-4 py-3 text-sm font-medium ${activeTab === 'text' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-600'}`}
          onClick={() => setActiveTab('text')}
        >
          Raw Results
        </button>
      </div>
      
      {/* Content Area */}
      <div className="p-4">
        {/* Raw Text Results Tab */}
        {activeTab === 'text' && (
          <div>
            <div className="results-content whitespace-pre-wrap mb-4">
              {formatResults(results)}
            </div>
            
            <div className="flex space-x-2 mt-4">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => {
                  const blob = new Blob([results], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `analysis-${jobId}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
              >
                Download Results
              </button>
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                onClick={() => {
                  navigator.clipboard.writeText(results);
                  alert('Results copied to clipboard');
                }}
              >
                Copy to Clipboard
              </button>
            </div>
          </div>
        )}
        
        {/* Summary Tab */}
        {activeTab === 'summary' && analysisData && (
          <div>
            {/* Overall Distribution Card */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Overall Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={overallDistributionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      >
                        {overallDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color || '#8884d8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Stats */}
                <div className="flex flex-col justify-center space-y-4">
                  <div className="border-l-4 border-blue-500 pl-3">
                    <div className="text-sm text-gray-600">EWS Total</div>
                    <div className="text-lg font-semibold">{totalEwsAmount || 'N/A'}</div>
                    <div className="text-sm text-gray-500">
                      {formatPercentage(analysisData.ewsPercentage / 100)} of total fund
                    </div>
                  </div>
                  
                  <div className="border-l-4 border-red-500 pl-3">
                    <div className="text-sm text-gray-600">Non-EWS Total</div>
                    <div className="text-lg font-semibold">{totalNonEwsAmount || 'N/A'}</div>
                    <div className="text-sm text-gray-500">
                      {formatPercentage(analysisData.nonEwsPercentage / 100)} of total fund
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* EWS Pillar Distribution Card */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">EWS Pillar Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Chart */}
                <div className="h-64">
                  {pieChartData && pieChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={pieChartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="value" name="Amount">
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || '#8884d8'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">No pillar distribution data available</p>
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-2">
                  {pillarDistribution && pillarDistribution.map((pillar, index) => (
                    pillar.amount > 0 && (
                      <div key={index} className="border-l-4 pl-2" style={{ borderColor: getPillarColor(pillar.name) }}>
                        <div className="text-xs font-medium text-gray-600">{pillar.name}</div>
                        <div className="text-sm font-semibold">{formatCurrency(pillar.amount)}</div>
                        <div className="text-xs text-gray-500">{formatPercentage(pillar.percentage / 100)}</div>
                      </div>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* EWS Details Tab */}
        {activeTab === 'details' && analysisData && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">EWS Detailed Analysis</h3>
            
            {groupedActivities && Object.keys(groupedActivities).length > 0 ? (
              <div className="space-y-6">
                {/* Group by pillar */}
                {Object.entries(groupedActivities).map(([pillar, activities]) => (
                  <div key={pillar} className="border-l-4 rounded-r-md shadow-sm pl-4 py-2" style={{ borderColor: getPillarColor(pillar) }}>
                    <h4 className="font-medium text-gray-800">{pillar}</h4>
                    
                    <div className="mt-2 space-y-3">
                      {activities.map((activity, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded">
                          {renderOutput(activity)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 italic">No detailed EWS data available.</div>
            )}
          </div>
        )}
        
        {/* Non-EWS Tab */}
        {activeTab === 'non-ews' && analysisData && (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Non-EWS Components</h3>
            
            {nonEwsDetails && nonEwsDetails.length > 0 ? (
              <div className="space-y-4">
                {nonEwsDetails.map((item, index) => (
                  <div key={index} className="output-item bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-200">
                    <div className="output-header mb-3">
                      <div className="output-title font-medium text-gray-900">{item['Output Description']}</div>
                      <div className="output-amount text-blue-600 font-semibold mt-1">{item.Amount}</div>
                    </div>
                    <div className="output-details grid grid-cols-1 gap-2 mb-3">
                      <div className="output-label text-sm text-gray-600">Reason for Exclusion:</div>
                      <div className="output-value text-sm text-gray-700">{item['Reason for Exclusion']}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 italic">No Non-EWS data available.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsViewer;